import { useFrame } from "@react-three/fiber";
import { forwardRef, useImperativeHandle, useRef } from "react";
import * as THREE from "three";

export interface RingCursorHandle {
  show: (point: THREE.Vector3, normal: THREE.Vector3) => void;
  hide: () => void;
  setActive: (active: boolean) => void;
  setRadius: (r: number) => void;
}

interface RingCursorProps {
  reducedMotion: boolean;
}

/**
 * The projected ring floating on the clay surface. Position + orientation are updated externally.
 * Breathes subtly at rest.
 */
export const RingCursor = forwardRef<RingCursorHandle, RingCursorProps>(function RingCursor(
  { reducedMotion },
  ref,
) {
  const groupRef = useRef<THREE.Group>(null);
  const innerRef = useRef<THREE.Mesh>(null);
  const activeRef = useRef(false);
  const visibleRef = useRef(false);
  const radiusRef = useRef(0.3);
  const targetRadiusRef = useRef(0.3);

  useImperativeHandle(ref, () => ({
    show(point, normal) {
      if (!groupRef.current) return;
      groupRef.current.visible = true;
      visibleRef.current = true;
      groupRef.current.position.copy(point);
      // Orient the ring to face outward along the surface normal.
      const up = new THREE.Vector3(0, 1, 0);
      const quat = new THREE.Quaternion().setFromUnitVectors(up, normal.clone().normalize());
      groupRef.current.quaternion.copy(quat);
    },
    hide() {
      if (!groupRef.current) return;
      groupRef.current.visible = false;
      visibleRef.current = false;
    },
    setActive(active) {
      activeRef.current = active;
    },
    setRadius(r) {
      targetRadiusRef.current = r;
    },
  }));

  useFrame((state, deltaRaw) => {
    const delta = Math.min(deltaRaw, 0.1);
    if (!groupRef.current || !visibleRef.current) return;
    // Smooth radius transition
    radiusRef.current += (targetRadiusRef.current - radiusRef.current) * 0.15;
    const breathe = reducedMotion ? 1 : 1 + Math.sin(state.clock.elapsedTime * 2.4) * 0.04;
    const s = radiusRef.current * breathe;
    groupRef.current.scale.set(s, s, s);
    if (innerRef.current) {
      const mat = innerRef.current.material as THREE.MeshBasicMaterial;
      const targetColor = activeRef.current ? new THREE.Color("#D4A574") : new THREE.Color("#FFFFFF");
      mat.color.lerp(targetColor, 0.15);
      const targetOpacity = activeRef.current ? 1.0 : 0.75;
      mat.opacity += (targetOpacity - mat.opacity) * 0.15 * (delta * 60);
    }
  });

  return (
    <group ref={groupRef} visible={false}>
      <mesh ref={innerRef} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[0.85, 1.0, 64]} />
        <meshBasicMaterial color="#FFFFFF" transparent opacity={0.75} depthTest={false} depthWrite={false} />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.001, 0]}>
        <ringGeometry args={[0.02, 0.06, 24]} />
        <meshBasicMaterial color="#D4A574" transparent opacity={0.9} depthTest={false} depthWrite={false} />
      </mesh>
    </group>
  );
});